/**
 * `@dxf-ai-editor/cad-core` — shared geometry primitives (DEFUSE S10b).
 *
 * Small, kind-agnostic math used by every per-kind bounds / grips / select
 * module: 2D point type, AABB accumulation, polar/parametric sampling, and
 * the "is this angle/parameter on the swept arc" test that every curved
 * primitive (Arc / Ellipse / bulge-arc segment) needs.
 *
 * Conventions (pinned so every kind file agrees):
 *   - Angles for Arc / polar helpers are DEGREES, CCW from +X, matching the
 *     DXF group-code convention (Autodesk DXF reference — ARC group 50/51)
 *     and `drawing-model/viewportMembership.ts`'s `polarPoint`. This is
 *     MODEL-SPACE (WCS) math — there is no Y-flip here; a Y-flip is a
 *     display-only transform the renderer applies later, never baked into
 *     canonical geometry (a Y-up canonical model is the whole point of a
 *     renderer-agnostic `cad-core`).
 *   - Ellipse parameters (`startParam`/`endParam`) are RADIANS, per the DXF
 *     ELLIPSE entity spec (groups 41/42) — the eccentric-anomaly-style
 *     parametrisation `P(t) = C + majorAxisVector·cos(t) + minorAxisVector·sin(t)`.
 */

import {
  boundsFromPoints as boundsFromPointsShared,
  ellipsePointAtDecomposed,
} from "@dxf-ai-editor/drawing-model";

import type { EntityBounds } from "./types";

export interface Vec2 {
  x: number;
  y: number;
}

/** Axis-aligned 2D bounding box. Alias of the public `EntityBounds` shape
 * (single definition lives in `types.ts`; internal modules use the shorter
 * `Aabb` name). */
export type Aabb = EntityBounds;

// KNOWN TOOLING LIMITATION (not a mathematical equivalence — targeted-cad-
// mutation-testing skill's "Equivalent mutant" contract, documented per its
// required fields, adapted for this distinct category):
//   file:line — common.ts, this line (`Math.PI * 2` — the module-scope
//     `TWO_PI` constant)
//   mutator — ArithmeticOperator (`*` → `/`)
//   why it survives Stryker despite being semantically load-bearing — this
//     is a MODULE-SCOPE `const`, evaluated exactly once when the module is
//     first imported into a test-runner worker process. Stryker's mutant
//     "switch" technique activates each mutant via a runtime flag checked
//     AT THE POINT the code executes — but this line only executes ONCE per
//     worker process lifetime (on first import), so a mutant activated
//     AFTER that first import can never re-trigger this specific
//     assignment. This is the exact "STATIC mutant (module-load-time code)"
//     gap this repo's `stryker.cad.config.mjs` already documents for
//     `coverageAnalysis` (a different but related manifestation).
//   validation (NOT an equivalence proof — a proof that REAL test coverage
//     exists despite the tool gap) — manually edited this line to
//     `Math.PI / 2` and ran the real (non-Stryker) `npx vitest run` suite
//     for this package: 11 of 136 tests failed immediately (`sweepRad`,
//     `angleOnSweepRad`, `ellipseBoundsRad`, and the Ellipse select-geometry
//     suite all depend on `TWO_PI`'s true value and diverge sharply under
//     the mutation) — reverted after confirming. The constant IS
//     thoroughly behaviourally tested; Stryker's specific mutant-switch
//     mechanism just cannot observe it for this AST node class.
// Stryker disable next-line ArithmeticOperator: module-scope const evaluated once per worker process — Stryker's switch-mutant activation cannot re-trigger it after first import (see tooling-limitation note above; real vitest coverage verified manually)
const TWO_PI = Math.PI * 2;

/**
 * Build an AABB from a non-empty list of finite 2D points. Empty/all-NaN ⇒
 * null. Re-exports `drawing-model/viewportMembership.ts`'s `boundsFromPoints`
 * — the exact same AABB accumulation the viewport-clip membership index
 * already uses — rather than re-deriving it (one-truth per CLAUDE.md
 * dup:check policy; the two independent copies were jscpd-flagged as a new
 * duplicate before this reuse).
 */
export function boundsFromPoints(points: readonly Vec2[]): Aabb | null {
  return boundsFromPointsShared(points);
}

/** Union of two AABBs (both must be finite boxes). */
export function unionBounds(a: Aabb, b: Aabb): Aabb {
  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY),
  };
}

/** Point on a circle of radius `r` about `(cx, cy)` at `angleDeg` (CCW from +X). */
export function polarPoint(
  cx: number,
  cy: number,
  r: number,
  angleDeg: number,
): Vec2 {
  const a = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

/**
 * Signed CCW sweep (DEGREES) from `startDeg` to `endDeg`, normalised into
 * (0, 360]. A zero or full-turn delta is treated as a full circle (360) —
 * matches `entityBounds.ts` / `viewportMembership.ts`'s Arc convention
 * (DXF ARC always sweeps CCW from start to end).
 */
export function sweepDeg(startDeg: number, endDeg: number): number {
  const raw = endDeg - startDeg;
  // A span that reaches (or exceeds) a full revolution sweeps the WHOLE
  // circle — reduce it to 360 BEFORE the `% 360` fold, which would otherwise
  // collapse a full (or over-full) turn to a short residual arc (Codex #467
  // P2: startDeg=0/endDeg=360 must be a full circle, not the 0° residual that
  // `entityModelBounds`'s inline `% 360` produced).
  if (Math.abs(raw) >= 360 - 1e-9) return 360;
  let sweep = (raw % 360 + 360) % 360;
  const rawNearZero = Math.abs(raw) < 1e-9;
  // EQUIVALENT MUTANT (targeted-cad-mutation-testing skill contract) — a
  // mutant changing this `<` to `<=` cannot be killed by any test:
  //   file:line — common.ts, this line (`sweep < 1e-9`)
  //   mutator — EqualityOperator (`<` → `<=`)
  //   why equivalent — `sweep` here is always produced by the
  //     `(raw % 360 + 360) % 360` fold immediately above, whose achievable
  //     output values near zero are quantized to the ULP-of-360 grid
  //     (doubles near magnitude 360 are spaced ~5.68e-14 apart — the
  //     rounding step inside `raw + 360` can only land on one of THOSE
  //     grid points, and the subsequent `% 360` recovers that grid point's
  //     offset from 360 exactly). The literal `1e-9` is NOT a multiple of
  //     that ~5.68e-14 spacing, so `sweep` can never land exactly ON `1e-9`
  //     for any `raw` — it always lands strictly above or strictly below,
  //     making `<` and `<=` observationally identical here.
  //   invariant — IEEE-754 double rounding of `raw + 360` (magnitude ~360)
  //     quantizes to steps of 2^(8-52) ≈ 5.684e-14; `1e-9 / 5.684e-14 ≈
  //     17594.7` is not an integer, so `1e-9` is provably off-grid.
  //   validation — empirically verified via exhaustive ULP-level scans (see
  //     this PR's mutation-gate investigation) across the reachable `raw`
  //     range near the boundary: no `raw` value makes `sweep === 1e-9`
  //     bit-for-bit.
  // Stryker disable next-line EqualityOperator: sweep can never land exactly on the 1e-9 literal (ULP-of-360 quantization proof above)
  const sweepNearZero = sweep < 1e-9;
  if (rawNearZero || sweepNearZero) sweep = 360;
  return sweep;
}

/** Whether `angleDeg` lies on the CCW arc `[startDeg, startDeg + sweepDeg]`. */
export function angleOnSweepDeg(
  angleDeg: number,
  startDeg: number,
  sweep: number,
): boolean {
  const rel = ((angleDeg - startDeg) % 360 + 360) % 360;
  // EQUIVALENT MUTANT — a mutant changing this `<=` to `<` cannot be killed:
  //   file:line — common.ts, this line (`rel <= sweep + 1e-9`)
  //   mutator — EqualityOperator (`<=` → `<`)
  //   why equivalent — same ULP-of-360-quantization argument as
  //     `sweepDeg`'s `sweepNearZero` (see that function's doc): `rel`'s
  //     achievable values near any target are quantized to the ULP-of-360
  //     grid (~5.68e-14 spacing) via the `% 360` fold above, while
  //     `sweep + 1e-9` is computed at whatever magnitude the caller's
  //     `sweep` is — for the two to collide EXACTLY at the `<=` boundary,
  //     `rel` would need to land bit-for-bit on the specific double
  //     `sweep + 1e-9`, which the fold's coarser quantization cannot
  //     reliably hit (verified: `rel`'s grid straddles, never touches, the
  //     target for every scanned neighbourhood).
  //   invariant — same as `sweepDeg`'s: IEEE-754 rounding of the `% 360`
  //     fold's intermediate `+360` step quantizes to ~5.68e-14 steps.
  //   validation — exhaustive ULP-level scan (this PR's mutation-gate
  //     investigation, `sweep = 90` fixture) found no `angleDeg` for which
  //     `rel === sweep + 1e-9` bit-for-bit; the achievable `rel` grid
  //     brackets the target on both sides without ever equalling it.
  // Stryker disable next-line EqualityOperator: rel can never land exactly on sweep+1e-9 (ULP-of-360 quantization proof above)
  return rel <= sweep + 1e-9;
}

/** Radians analogue of {@link sweepDeg} for the Ellipse start/end parameter. */
export function sweepRad(startRad: number, endRad: number): number {
  const raw = endRad - startRad;
  // A parameter span reaching (or exceeding) a full revolution sweeps the
  // WHOLE ellipse — even a NON-multiple over-turn like `[0, 7 rad]` (Codex
  // #467 P2). The live tessellator (`arcEllipseSpline.ts::tessellateEllipse`)
  // walks the positive raw span (`sweep = endParam - startParam`, no modulo)
  // and draws at least a full ellipse, so bounds must span every parametric
  // extremum — NOT the `raw % 2π` short arc (7 rad would fold to ≈0.72 rad and
  // under-report the ellipse to a sliver). Reduce to a full turn BEFORE the
  // `% 2π` fold.
  if (Math.abs(raw) >= TWO_PI - 1e-9) return TWO_PI;
  let sweep = (raw % TWO_PI + TWO_PI) % TWO_PI;
  const rawNearZero = Math.abs(raw) < 1e-9;
  // EQUIVALENT MUTANT — radians analogue of `sweepDeg`'s `sweepNearZero`
  // (see that function's doc for the full ULP-quantization proof; here the
  // fold is `% TWO_PI` instead of `% 360`, so the achievable grid is spaced
  // by ULP-of-`TWO_PI` ≈ 8.88e-16 instead of ULP-of-360 — a DIFFERENT, even
  // finer grid, but `1e-9` is equally off-grid (`1e-9 / 8.88e-16 ≈
  // 1,126,198` — not an integer), and empirically verified (exhaustive
  // scan over 2,000,000 ULPs straddling the boundary from both directions)
  // to never independently trigger this fold when `rawNearZero` is false.
  // Stryker disable next-line EqualityOperator: sweep can never land exactly on the 1e-9 literal (ULP-of-TWO_PI quantization, see sweepDeg's doc)
  // Stryker disable next-line ConditionalExpression: forcing this to `false` unconditionally is unobservable — `sweepNearZero` never independently triggers the fold below (it is only ever true when `rawNearZero` is already true, per the exhaustive ULP-level scan above), so collapsing its computed value to `false` produces the SAME `if` outcome for every reachable input as leaving it correctly computed
  const sweepNearZero = sweep < 1e-9;
  // Stryker disable next-line ConditionalExpression: `sweepNearZero` never independently triggers this fold (verified via exhaustive ULP-level scan straddling the boundary from both directions — it is only ever true when `rawNearZero` is already true)
  if (rawNearZero || sweepNearZero) sweep = TWO_PI;
  return sweep;
}

/** Radians analogue of {@link angleOnSweepDeg}. */
export function angleOnSweepRad(
  angleRad: number,
  startRad: number,
  sweep: number,
): boolean {
  const rel = ((angleRad - startRad) % TWO_PI + TWO_PI) % TWO_PI;
  return rel <= sweep + 1e-9;
}

/**
 * Exact AABB of a circular arc `[startDeg, endDeg]` (CCW, DXF convention):
 * the two endpoints plus every cardinal angle (0/90/180/270) the sweep
 * actually crosses (the arc bows outside the endpoint-only box). Mirrors
 * `entityBounds.ts`'s Arc case / `viewportMembership.ts`'s `arcBounds`.
 */
export function arcBoundsDeg(
  cx: number,
  cy: number,
  r: number,
  startDeg: number,
  endDeg: number,
): Aabb | null {
  const sweep = sweepDeg(startDeg, endDeg);
  const pts: Vec2[] = [
    polarPoint(cx, cy, r, startDeg),
    polarPoint(cx, cy, r, endDeg),
  ];
  for (const cardinal of [0, 90, 180, 270]) {
    if (angleOnSweepDeg(cardinal, startDeg, sweep)) {
      pts.push(polarPoint(cx, cy, r, cardinal));
    }
  }
  return boundsFromPoints(pts);
}

/** Evenly-spaced sample points along a circular arc, for select-geometry tessellation. */
export function tessellateArcDeg(
  cx: number,
  cy: number,
  r: number,
  startDeg: number,
  endDeg: number,
  stepsHint?: number,
): Vec2[] {
  const sweep = sweepDeg(startDeg, endDeg);
  const steps = Math.max(6, stepsHint ?? Math.ceil(sweep / 20));
  const pts: Vec2[] = [];
  for (let i = 0; i <= steps; i++) {
    pts.push(polarPoint(cx, cy, r, startDeg + (sweep * i) / steps));
  }
  return pts;
}

/**
 * Ellipse (or elliptic-arc) point at parameter `t` (radians), per the DXF
 * ELLIPSE parametrisation `P(t) = C + majorAxisVector·cos(t) + minorAxisVector·sin(t)`
 * where `minorAxisVector` is `majorAxisVector` rotated +90° and scaled by
 * `ratio`. `cosF`/`sinF` are the pre-normalised major-axis direction
 * cosine/sine (`majorX/mag`, `majorY/mag`).
 *
 * Re-exports `drawing-model`'s `ellipsePointAtDecomposed` (T1 arm (c) dup:check convergence —
 * `drawing-model`'s own WCS-bounds helper needed the identical decomposed-parameter formula;
 * cad-core already depends on drawing-model, the correct direction, so the formula moved to
 * the one-truth home instead of staying duplicated here) rather than re-deriving the formula.
 */
export function ellipsePointAt(
  cx: number,
  cy: number,
  majorMag: number,
  minorMag: number,
  cosF: number,
  sinF: number,
  t: number,
): Vec2 {
  return ellipsePointAtDecomposed(cx, cy, majorMag, minorMag, cosF, sinF, t);
}

/**
 * Exact AABB of an ellipse / elliptic arc over parameter range
 * `[startParam, endParam]` (radians, CCW, DXF convention). Finds the
 * parametric critical points (dx/dt = 0, dy/dt = 0 — the ellipse's own
 * "cardinal" extrema) that fall on the swept range, plus both endpoints.
 */
export function ellipseBoundsRad(
  cx: number,
  cy: number,
  majorX: number,
  majorY: number,
  ratio: number,
  startParam: number,
  endParam: number,
): Aabb | null {
  const majorMag = Math.hypot(majorX, majorY);
  if (majorMag === 0) return { minX: cx, minY: cy, maxX: cx, maxY: cy };
  const cosF = majorX / majorMag;
  const sinF = majorY / majorMag;
  const minorMag = majorMag * ratio;

  const point = (t: number): Vec2 =>
    ellipsePointAt(cx, cy, majorMag, minorMag, cosF, sinF, t);

  const sweep = sweepRad(startParam, endParam);
  const pts: Vec2[] = [point(startParam), point(endParam)];

  // dx/dt = 0 ⇒ tan(t) = -(minorMag·sinF) / (majorMag·cosF)
  const tx = Math.atan2(-minorMag * sinF, majorMag * cosF);
  // dy/dt = 0 ⇒ tan(t) = (minorMag·cosF) / (majorMag·sinF)
  const ty = Math.atan2(minorMag * cosF, majorMag * sinF);
  // EQUIVALENT MUTANT (targeted-cad-mutation-testing skill, documented per
  // its "Equivalent mutant" contract) — a mutant flipping either `+ Math.PI`
  // below to `- Math.PI` (common.ts, this line, both occurrences) cannot be
  // killed by any behavioural test:
  //   file:line — common.ts:226 (`tx + Math.PI` / `ty + Math.PI`)
  //   mutator — ArithmeticOperator (`+` → `-`)
  //   why equivalent — `tx + Math.PI` and `tx - Math.PI` name the SAME
  //     angle modulo 2π (they differ by exactly one full turn); EVERY
  //     consumer of this value is 2π-periodic — `angleOnSweepRad`'s own
  //     `% TWO_PI` fold immediately below, and `Math.cos`/`Math.sin` inside
  //     `ellipsePointAt`'s `point(t)` call — so the sign choice on this
  //     specific `Math.PI` offset is unobservable in this function's output.
  //   invariant — `Math.atan2` (the only producer of `tx`/`ty` in this
  //     codebase) always returns a value in `[-π, π]`, so `tx ± π` never
  //     hits a large-magnitude input where floating-point cancellation could
  //     make the two forms diverge beyond ULP noise.
  //   validation — `common.test.ts`'s `ellipseBoundsRad` suite empirically
  //     probed 200 random angles plus this function's own fixture values:
  //     `Math.cos`/`Math.sin` of `t+π` vs `t-π`, and `angleOnSweepRad`'s
  //     folded `rel` for each, agree to ~1e-15 (float64 ULP) — many orders
  //     below this module's own `1e-9` epsilon and any `toBeCloseTo`
  //     precision used across this package's suite. A test could only
  //     "distinguish" the two forms by asserting on that sub-epsilon noise,
  //     which would be a platform-dependent tautology, not a real check.
  // Stryker disable next-line ArithmeticOperator: tx±π / ty±π name the same angle mod 2π — see the equivalent-mutant proof above
  for (const t of [tx, tx + Math.PI, ty, ty + Math.PI]) {
    if (angleOnSweepRad(t, startParam, sweep)) pts.push(point(t));
  }
  return boundsFromPoints(pts);
}

/** Evenly-spaced sample points along an ellipse / elliptic arc, for select-geometry. */
export function tessellateEllipseRad(
  cx: number,
  cy: number,
  majorX: number,
  majorY: number,
  ratio: number,
  startParam: number,
  endParam: number,
  stepsHint = 32,
): Vec2[] {
  const majorMag = Math.hypot(majorX, majorY);
  if (majorMag === 0) return [{ x: cx, y: cy }];
  const cosF = majorX / majorMag;
  const sinF = majorY / majorMag;
  const minorMag = majorMag * ratio;
  const sweep = sweepRad(startParam, endParam);
  const steps = Math.max(8, stepsHint);
  const pts: Vec2[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = startParam + (sweep * i) / steps;
    pts.push(ellipsePointAt(cx, cy, majorMag, minorMag, cosF, sinF, t));
  }
  return pts;
}

/**
 * Unit vector from `(x, y)`; a degenerate (near-zero magnitude) input falls
 * back to `(1, 0)` rather than emitting `NaN`.
 *
 * Relocated from `apps/web/.../scene3d/adapters/geom/vecMath.ts` (which
 * re-exports it, so no import path changed) when the wide-polyline BAND math
 * moved into this package: `./polylineWidthOutline.ts` needs it, and `cad-core`
 * may not import `apps/*` (`.dependency-cruiser.cjs::cad-core-no-back-edges`).
 * One implementation, three callers — the DIMENSION and MULTILEADER geometry
 * builders in the app keep theirs through the re-export.
 */
export function unitVec2(x: number, y: number): Vec2 {
  const m = Math.hypot(x, y);
  if (m < 1e-9) return { x: 1, y: 0 };
  return { x: x / m, y: y / m };
}

/** Midpoint of two points. */
export function midpoint(a: Vec2, b: Vec2): Vec2 {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/** Rotate a vector `(x, y)` by `angleDeg` (CCW, DXF convention). */
export function rotateDeg(x: number, y: number, angleDeg: number): Vec2 {
  const a = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  return { x: x * cos - y * sin, y: x * sin + y * cos };
}

/** Convert radians to degrees. */
export function radToDeg(rad: number): number {
  return (rad * 180) / Math.PI;
}

/**
 * Rotate a vector `(x, y)` by `angleRad` RADIANS (CCW) — the radians-native
 * sibling of {@link rotateDeg}. Round-2 of this PR briefly removed this pair
 * (a blanket always-degrees `axisAngle` collapse made it look dead), but
 * round-4 restored the discriminant `rotateByArrayAxisAngle`/
 * `arrayAxisAngleDeg` need for the one residual RADIANS case (an associative
 * array's `axisAngle` resolved from the standalone positional params record,
 * never unit-converted) — see that pair's doc for the full case split.
 */
export function rotateRad(x: number, y: number, angleRad: number): Vec2 {
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  return { x: x * cos - y * sin, y: x * sin + y * cos };
}

/**
 * `ArrayRectEntity.axisAngle` carries TWO DIFFERENT UNITS depending on
 * WHICH DXF form (if any) the import-time graph resolver decoded it from —
 * a genuine, confirmed mismatch, tracked across FOUR rounds (Codex #467
 * round-2 P2 "Preserve degree-based ArrayRect angles"; superseded for the
 * named-form case by AW-1's units seam; round-4 P2 restored the discriminant
 * below after a blanket always-degrees collapse regressed the positional-
 * only case). The three-way split, using
 * `ResolvedAssocArray.axisAngleFromNamedForm` (`dxf-io/handleResolution.ts`)
 * projected onto the model as `ArrayRectEntity.axisAngleFromNamedForm`:
 *
 *   - `associative: true` AND `axisAngleFromNamedForm: true` — the graph's
 *     named "AxesAngle" value-param resolved (`dxf-io/handleResolution.ts`'s
 *     `mergeNamedRectValueParams`) and AW-1's units seam
 *     (`axesAngleRadiansToModelDegrees`) converted AutoCAD's on-wire RADIANS
 *     to the model's DEGREES convention at import time, matching the DXF
 *     group-50 INSERT-rotation convention `axisAngle` uses everywhere else.
 *     **DEGREES**.
 *   - `associative: true` AND `assocParamsSnapshot?.axisAngle !== undefined`
 *     (a value resolved) BUT `axisAngleFromNamedForm` falsy — the value came
 *     from the standalone `ACDBASSOCARRAYRECTANGULARPARAMETERS` positional
 *     record instead. AW-1 deliberately does NOT unit-convert that form —
 *     "no fixture proves its axisAngle unit convention" (`handleResolution
 *     .ts`'s `axesAngleRadiansToModelDegrees` doc, a synthetic/ODA-sample
 *     shape real AutoCAD never emits) — so this ONE case preserves the
 *     historical (pre-AW-1) assumption rather than silently flipping it:
 *     **RADIANS**.
 *   - Every other case — `associative: true` but NEITHER form resolved
 *     axisAngle at all (an expression-driven/multi-value named param the
 *     formula-chain guard skipped, or a malformed/partial graph —
 *     `entity.axisAngle` still holds the anchor block-reference's OWN raw
 *     group-50 rotation), OR `associative: false` (a bare block-ref grid,
 *     `axisAngle` copied from its own `rotation` group) — **DEGREES** (group
 *     50 is always degrees, associative or not).
 *
 * Every cad-core consumer of `axisAngle` (bounds/grips/select) MUST branch
 * through this ONE helper rather than each re-deriving its own rotate call.
 *
 * NEUTRAL-BASELINE CORRECTION (round 9, layered on TOP of the three-way unit
 * split above — Codex P2 local review: "cad-core array angle semantics out
 * of sync with the render/properties baseline fix"). The associative
 * `axisAngle` value (in WHICHEVER of the two above units it's actually in)
 * is NOT the array's absolute visual rotation directly — per the resolved
 * `AcDbAssocArrayRectangularParameters` "AxesAngle" ODA semantics
 * (`CalculateDefaultMatrix`: `Y-axis = X-axis-direction.rotateBy(AxesAngle)`),
 * it is the angle BETWEEN the row and column axes, and a STANDARD orthogonal
 * array (rows perpendicular to columns — the overwhelming common case,
 * confirmed byte-identical against real AutoCAD fixtures in
 * `drawing-model/dxfBridge.arrayAssoc.test.ts`) persists AxesAngle = π/2
 * (90°) even though it is VISUALLY unrotated (columns along +X, rows along
 * +Y — the same layout a non-associative array's 0° neutral produces).
 * Rotating column/row offset vectors by the RAW π/2 (the pre-round-9
 * behaviour) therefore visually rotates a standard array's grid by 90° for
 * no authored reason. Both helpers below subtract a π/2 (radians branch) or
 * 90° (degrees branch) baseline for EVERY `associative: true` entity —
 * whichever of the two units {@link isArrayAxisAngleRadians} selected, so the
 * correction is applied in THAT SAME unit before any conversion — so bounds,
 * grips and selection (this package's three consumers) agree with EACH OTHER
 * for the SAME entity, across all three `isArrayAxisAngleRadians` cases.
 *
 * An `associative: true` entity's `axisAngle` is guaranteed to carry ONE OF:
 * a real decoded AxesAngle; the anchor block-reference's OWN real authored
 * (non-zero) rotation, preserved verbatim when the graph never resolved
 * AxesAngle at all (AW-1 P1 round-2, `dxfBridge.arrayAssoc.test.ts`);
 * or, only when NEITHER of those carries any information (graph unresolved
 * AND the anchor itself is unrotated), the ObjectARX producer default of
 * π/2 (`drawing-model/dxfBridge.arrayAssocImport.ts`
 * `ASSOC_ARRAY_OMITTED_AXISANGLE_DEFAULT`, round 9 sibling of the #251-B
 * omitted-spacing default) — never the schema's unrelated, ungrounded
 * non-associative `0`. So this correction is safe to apply unconditionally
 * for every `associative: true` entity cad-core observes, regardless of
 * which of the three {@link isArrayAxisAngleRadians} cases it fell into.
 *
 * KNOWN RESIDUAL GAP, documented not fixed here (outside this merge's file
 * boundary — `.dependency-cruiser.cjs` `cad-core-no-back-edges` forbids
 * cad-core from importing `apps/web`/`command-engine`, but NOT the reverse;
 * this gap is a scope decision, not an architectural one): `command-engine
 * /src/descriptors/containers.ts` (`resolveArrayAxisAngleDisplayDeg`/
 * `arrayAxisAngleDisplayDegToRaw`, the Properties-panel "Axis angle" field)
 * and `apps/web` `dxfGeometry.ts::buildArrayRectPreview` /
 * `scene/renderHelpers.ts::resolveArrayAxisAngleDeg` (the legacy string-bag
 * preview + live renderer) do NOT yet know about
 * {@link isArrayAxisAngleRadians} — they still branch on `associative` alone
 * (unconditionally treating it as the pre-AW-1 RADIANS-with-π/2-baseline
 * case), so for a real NAMED-FORM-resolved import (the DOMINANT
 * real-AutoCAD decode path, now stored in DEGREES per AW-1's units seam)
 * those three sites double-convert an already-degrees value. This mirrors
 * the SAME already-documented, already-deliberate deferral
 * `apps/web/src/app/scene3d/grips/gripsOverlay.tsx`'s own `buildGripSet`
 * comment records for ARRAYRECT's legacy grips ("This wiring has NOT been
 * switched to the typed cad-core path here... out of AW-1's scope... a
 * follow-up PR migrating ARRAYRECT onto the typed grip path should
 * re-verify") — wiring these three sites onto the shared discriminant is a
 * live-render/Properties-panel behaviour change (CLAUDE.md Category C,
 * visual gate required), correctly out of scope for a cad-core-math merge,
 * left for that same follow-up PR.
 */
export function rotateByArrayAxisAngle(
  x: number,
  y: number,
  e: {
    axisAngle: number;
    associative: boolean;
    assocParamsSnapshot?: { axisAngle?: number };
    axisAngleFromNamedForm?: boolean;
  },
): Vec2 {
  if (!e.associative) return rotateDeg(x, y, e.axisAngle);
  return isArrayAxisAngleRadians(e)
    ? rotateRad(x, y, e.axisAngle - Math.PI / 2)
    : rotateDeg(x, y, e.axisAngle - 90);
}

/**
 * `e.axisAngle` normalised to DEGREES for DISPLAY (e.g. a grip's
 * `rotationDeg` field) — the degrees-native sibling of
 * {@link rotateByArrayAxisAngle}. See that function's doc for the unit split
 * AND the round-9 neutral-baseline correction this mirrors.
 */
export function arrayAxisAngleDeg(e: {
  axisAngle: number;
  associative: boolean;
  assocParamsSnapshot?: { axisAngle?: number };
  axisAngleFromNamedForm?: boolean;
}): number {
  if (!e.associative) return e.axisAngle;
  return isArrayAxisAngleRadians(e) ? radToDeg(e.axisAngle) - 90 : e.axisAngle - 90;
}

/** Shared discriminant for {@link rotateByArrayAxisAngle}/{@link arrayAxisAngleDeg} — see that doc for the full case split. */
function isArrayAxisAngleRadians(e: {
  associative: boolean;
  assocParamsSnapshot?: { axisAngle?: number };
  axisAngleFromNamedForm?: boolean;
}): boolean {
  return (
    e.associative &&
    e.assocParamsSnapshot?.axisAngle !== undefined &&
    !e.axisAngleFromNamedForm
  );
}

/** Segments (open polyline) connecting consecutive points, closing the loop when `closed`. */
export function segmentsFromPoints(
  points: readonly Vec2[],
  closed: boolean,
): Array<{ start: Vec2; end: Vec2 }> {
  const segments: Array<{ start: Vec2; end: Vec2 }> = [];
  // EQUIVALENT MUTANT — a mutant changing this `<` to `<=` cannot be
  // killed: for a real JS `Array`, reading one index PAST the declared
  // length (`points[points.length]`) is ALWAYS `undefined` (a genuine
  // out-of-range numeric property cannot coexist with a shorter `.length`
  // — assigning it would auto-extend `.length` itself). The extra
  // iteration this mutant adds always sets `end = undefined`, which the
  // `start && end` guard two lines below unconditionally filters out — so
  // the returned `segments` array is bit-for-bit identical either way, for
  // every possible `points` input (dense or sparse).
  // Stryker disable next-line EqualityOperator: the extra iteration always sets `end = undefined`, filtered by the `start && end` guard below — see proof above
  for (let i = 1; i < points.length; i++) {
    const start = points[i - 1];
    const end = points[i];
    if (start && end) segments.push({ start, end });
  }
  if (closed && points.length >= 2) {
    const start = points[points.length - 1];
    const end = points[0];
    if (start && end) segments.push({ start, end });
  }
  return segments;
}
